import "./i18n";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { deriveReservedPartnerRouteSegmentsFromRouteTree } from "@pandas/core-ui/route";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { I18nextProvider } from "react-i18next";
import { ErrorBoundary } from "./components/core/ErrorBoundary";
import Loader from "./components/core/Loaders/Loader";
import { servicesOrchestratorValues } from "./config/services";
import i18next from "./i18n";
import { routeTree } from "./routeTree.gen";
import "@pandas/core-ui/fonts.css";
import "./index.css";

const queryClient = new QueryClient();
const reservedPartnerRouteSegments = deriveReservedPartnerRouteSegmentsFromRouteTree(routeTree);

const router = createRouter({
	routeTree,
	basepath: "/",
	trailingSlash: "never",
	context: {
		auth: undefined!,
		reservedPartnerRouteSegments,
	},
});

declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}

function InnerApp() {
	const auth = useAuth0();

	if (auth.isLoading) {
		return (
			<div className="flex h-screen items-center justify-center">
				<Loader />
			</div>
		);
	}

	router.update({ context: { auth, reservedPartnerRouteSegments } });

	return (
		<RouterProvider
			router={router}
			context={{ auth, reservedPartnerRouteSegments }}
		/>
	);
}

createRoot(document.getElementById("root")!).render(
	<StrictMode>
		<ErrorBoundary>
			<Auth0Provider
				domain={servicesOrchestratorValues.auth0.domain}
				clientId={servicesOrchestratorValues.auth0.clientId}
				cacheLocation="localstorage"
				onRedirectCallback={(appState) => {
					window.history.replaceState({}, "", appState?.returnTo ?? "/");
				}}
				authorizationParams={{
					redirect_uri: window.location.origin,
					audience: servicesOrchestratorValues.auth0.audience,
				}}
			>
				<QueryClientProvider client={queryClient}>
					<I18nextProvider i18n={i18next}>
						<InnerApp />
					</I18nextProvider>
				</QueryClientProvider>
			</Auth0Provider>
		</ErrorBoundary>
	</StrictMode>,
);
